// FullEditor.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import grapesjs from 'grapesjs';
import Cookies from 'js-cookie';
import axios from 'axios';
import 'grapesjs/dist/css/grapes.min.css';
import toast from 'react-hot-toast';
import { Spinner } from 'react-bootstrap';
import blocksBasic from 'grapesjs-blocks-basic';


// Section names become the Firebase section_folder, so restrict the charset:
// must start with a letter, then only letters, digits, and underscores.
const SECTION_NAME_RE = /^[A-Za-z][A-Za-z0-9_]*$/;

const FullEditor = ({ app_url }) => {
  const editorRef = useRef(null);
  const scriptRef = useRef('');
  const originalStyleRef = useRef('');
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [copiedBtnLoading, setCopiedBtnLoading] = useState(false);
  const [copiedSectionName, setCopiedSectionName] = useState(null);
  const [currentDevice, setCurrentDevice] = useState('desktop');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyModalInput, setCopyModalInput] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Reject scripts that look like HTML documents or SPA fallback markup
  const isProbablyJS = useCallback((txt) => {
    if (!txt || typeof txt !== 'string') return false;
    const lowered = txt.trim().toLowerCase();
    if (
      lowered.includes('<!doctype') ||
      lowered.includes('<html') ||
      lowered.includes('</html>') ||
      lowered.includes('<head') ||
      lowered.includes('<body') ||
      lowered.includes('</body>') ||
      lowered.includes('</head>') ||
      lowered.includes('</script>')
    ) return false;
    return true;
  }, []);

  //////////////////////////Senitize html content////////////////////////
  // Sanitize HTML content - normalize Unicode characters and HTML entities
  const sanitizeHtmlContent = useCallback((html) => {
    if (!html || typeof html !== 'string') return html;
    return html
      // Normalize Unicode minus characters
      .replace(/\u2212/g, '-')           // Unicode minus sign (U+2212)
      .replace(/âˆ'/g, '-')               // Corrupted UTF-8 encoding (minus)
      .replace(/âˆ'/g, '-')
      // Normalize Unicode arrow characters to HTML entities or ASCII
      .replace(/\u25C0/g, '&#9664;')    // ◀ (Black Left-Pointing Triangle)
      .replace(/\u25B6/g, '&#9654;')    // ▶ (Black Right-Pointing Triangle)
      .replace(/\u25B8/g, '&#9654;')    // ▸ (Small Right-Pointing Triangle)
      .replace(/\u25C2/g, '&#9664;')    // ◂ (Small Left-Pointing Triangle)
      .replace(/â—€/g, '&#9664;')       // Corrupted left arrow encoding
      .replace(/â–¶/g, '&#9654;')       // Corrupted right arrow encoding
      // Ensure HTML entities are properly formatted
      .replace(/&#9664;/g, '&lt;')      // Replace with simple < (or keep &#9664; if you prefer)
      .replace(/&#9654;/g, '&gt;')      // Replace with simple > (or keep &#9654; if you prefer)
    // Alternative: Use text-based arrows that won't corrupt
    // Or use the HTML entities directly if they're already correct
  }, []);

  /////////////////////////End of Sanitize html content////////////////////////

  /** --- Clean CSS, keep @rules & sxi classes --- */
  const cleanCssOutput = useCallback((rawCss) => {
    if (!rawCss) return '';
    const rules = [];
    let currentRule = '';
    let inAtRule = false;
    let braceCount = 0;
    for (let i = 0; i < rawCss.length; i++) {
      const char = rawCss[i];
      if (char === '@') inAtRule = true;
      if (char === '{') braceCount++;
      if (char === '}') {
        braceCount--;
        if (braceCount === 0 && inAtRule) {
          currentRule += char;
          rules.push(currentRule);
          currentRule = '';
          inAtRule = false;
          continue;
        }
      }
      currentRule += char;
      if (char === '}' && braceCount === 0 && !inAtRule) {
        rules.push(currentRule);
        currentRule = '';
      }
    }
    if (currentRule) rules.push(currentRule);

    return rules
      .filter(rule => {
        const t = rule.trim();
        if (!t) return false;
        if (t.startsWith('@')) return true; // Keep @media, @font-face
        if (
          t.includes('.gjs-') ||
          t.includes('[class*="gjs-"]') ||
          (t.startsWith('*') && !t.includes('.sxi-')) ||
          (t.startsWith('body') && !t.includes('.sxi-'))
        ) return false;
        // Strip grid layout classes — always re-injected by gridCss at save time
        if (t.includes('.sxi-gjs-row') || t.includes('.sxi-gjs-cell')) return false;
        return true;
      })
      .join('\n')
      .trim();
  }, []);

  /** Remove IDs but keep data attributes, carousel/FAQ IDs, and sxi- prefixed IDs */
  /** Remove IDs but keep data attributes, carousel/FAQ IDs, and sxi- prefixed IDs */
  const removeIdsFromHtml = useCallback(html => {
    // Use negative lookahead to exclude FAQ/carousel IDs and sxi- prefixed IDs from removal
    // Remove IDs that are NOT FAQ/carousel related AND NOT sxi- prefixed (for layout)
    // Preserve: answer_*, toggle_*, sxi_dynamic_carousel_*, dynamic_template_faq_*, and any sxi-* IDs
    return html.replace(/ id="(?!answer_\d+_\d+|toggle_\d+_\d+|sxi_dynamic_carousel_\d+|dynamic_template_faq_\d+|sxi-[^"]*)[^"]*"/g, '');
  }, []);

  /** Combine HTML, CSS, JS */
  const generateFinalHtml = useCallback((body, css, js) => {
    // Extract FAQ initial display states from CSS rules like #answer_3_0{display:block;}
    // Check if body contains FAQ elements to determine if this is a FAQ section
    const hasFAQElements = body.includes('sxi_dynamic_faq') || body.includes('dynamic_template_faq');

    // Only inject FAQ initialization script for FAQ sections
    const initialStateScript = hasFAQElements ? `
    // Initialize FAQ display states from CSS on page load
    (function initFAQStates() {
      const styleSheets = document.styleSheets;
      for (let sheet of styleSheets) {
        try {
          const rules = sheet.cssRules || sheet.rules;
          for (let rule of rules) {
            if (rule.selectorText && rule.selectorText.startsWith('#answer_')) {
              const id = rule.selectorText.replace('#', '');
              const answer = document.getElementById(id);
              const display = rule.style.display || (rule.style.cssText.match(/display:\s*(block|none)/)?.[1] || 'none');
              
              if (answer) {
                answer.style.display = display;
                const toggleId = id.replace('answer_', 'toggle_');
                const toggle = document.getElementById(toggleId);
                if (toggle) {
                  toggle.textContent = display === 'block' ? '-' : '+';
                }
              }
            }
          }
        } catch (e) {
          // Cross-origin stylesheets may throw errors, ignore them
        }
      }
    })();
  `: '';

    return `
    <html>
      <head><style>${css}</style></head>
      ${body}
      <script>${initialStateScript}${js}</script>
    </html>
  `;
  }, []);

  /** --- Inject JS into iframe safely --- */
  const injectJs = useCallback(() => {
    if (!editorRef.current) return;
    const iframe = editorRef.current.Canvas.getFrameEl();
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    if (!doc) return;

    let scriptTag = doc.getElementById('custom-script');
    if (!scriptTag) {
      scriptTag = doc.createElement('script');
      scriptTag.id = 'custom-script';
      doc.body.appendChild(scriptTag);
    }
    const safeScript = isProbablyJS(scriptRef.current) ? scriptRef.current : '';
    scriptTag.innerHTML = safeScript;
    console.log('Injected JS:', scriptRef.current);
  }, [isProbablyJS]);

  const normalizeVimeoUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return url;
    const trimmed = url.trim();
    const vimeoMatch = trimmed.match(/^(?:https?:)?\/\/(?:www\.)?(?:player\.vimeo\.com\/video\/|vimeo\.com\/(?:.*?\/)?)(\d+)([?&#].*)?$/i);
    if (!vimeoMatch) return trimmed;

    const videoId = vimeoMatch[1];
    const query = vimeoMatch[2] || '';
    return `https://player.vimeo.com/video/${videoId}${query}`;
  }, []);

  const normalizeVimeoEmbedUrls = useCallback((html) => {
    if (!html || typeof html !== 'string') return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const elements = Array.from(doc.querySelectorAll('[src], [data-src], [href], [srcset]'));

    elements.forEach((el) => {
      const candidateAttrs = ['src', 'data-src', 'data-lazy-src', 'href'];
      let normalizedValue = null;

      candidateAttrs.forEach((attr) => {
        if (!el.hasAttribute(attr)) return;
        const value = el.getAttribute(attr) || '';
        const normalized = normalizeVimeoUrl(value);
        if (normalized && normalized !== value) {
          if (!normalizedValue) normalizedValue = normalized;
          el.setAttribute(attr, normalized);
        }
      });

      if (!el.hasAttribute('src') && normalizedValue) {
        el.setAttribute('src', normalizedValue);
      }

      if (el.hasAttribute('srcset')) {
        const value = el.getAttribute('srcset') || '';
        const normalized = value
          .split(',')
          .map((part) => {
            const [srcPart, descriptor] = part.trim().split(/\s+/);
            const normalizedSrc = normalizeVimeoUrl(srcPart) || srcPart;
            return descriptor ? `${normalizedSrc} ${descriptor}` : normalizedSrc;
          })
          .join(', ');
        if (normalized !== value) {
          el.setAttribute('srcset', normalized);
        }
      }
    });

    const iframes = Array.from(doc.querySelectorAll('iframe'));
    iframes.forEach((iframe) => {
      const src = iframe.getAttribute('src') || '';
      const dataSrc = iframe.getAttribute('data-src') || iframe.getAttribute('data-lazy-src') || '';
      const normalizedSrc = normalizeVimeoUrl(src) || normalizeVimeoUrl(dataSrc);
      if (normalizedSrc) {
        iframe.setAttribute('src', normalizedSrc);
      }
      if (!iframe.hasAttribute('allow')) {
        iframe.setAttribute('allow', 'autoplay; fullscreen');
      }
      if (!iframe.hasAttribute('allowfullscreen')) {
        iframe.setAttribute('allowfullscreen', '');
      }
      if (!iframe.hasAttribute('frameborder')) {
        iframe.setAttribute('frameborder', '0');
      }
      iframe.style.display = iframe.style.display || 'block';
      iframe.style.width = iframe.style.width || '100%';
      iframe.style.minHeight = iframe.style.minHeight || '200px';
    });

    return doc.documentElement.outerHTML;
  }, [normalizeVimeoUrl]);

  const base64EncodeUnicode = useCallback((str) => {
    if (!str) return '';
    try {
      return window.btoa(unescape(encodeURIComponent(str)));
    } catch (e) {
      console.warn('Unicode base64 encode failed, falling back to plain btoa', e);
      return window.btoa(str);
    }
  }, []);

  /** --- Initialize GrapesJS --- */
  useEffect(() => {
    const fetchAndLoadHtml = async () => {
      const htmlText = normalizeVimeoEmbedUrls(
        sessionStorage.getItem('current_edit_div') || '<div>Edit your component</div>'
      );
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      // Gather styles and scripts
      const style = Array.from(doc.querySelectorAll('style'))
        .map(s => s.innerHTML)
        .join('\n') || '';
      originalStyleRef.current = style;

      const body = doc.querySelector('body')?.innerHTML || '';
      const script = Array.from(doc.querySelectorAll('script'))
        .map(s => s.innerHTML)
        .join('\n') || '';
      scriptRef.current = isProbablyJS(script) ? script : '';

      editorRef.current = grapesjs.init({
        container: '#gjs',
        height: '100vh',
        width: '100%',
        fromElement: false,
        storageManager: false,
        selectorManager: { componentFirst: true, escapeName: n => n },
        components: body,
        style: style,
        plugins: [(editor) => blocksBasic(editor, { stylePrefix: 'gjs-', addBasicStyle: false })],
        avoidInlineStyle: true,
        clearStyles: false,
        styleManager: {
          sectors: [
            {
              name: 'Dimensions',
              open: false,
              properties: [
                { property: 'width',      type: 'number', units: ['px', '%', 'vw', 'em'],  defaults: 'auto' },
                { property: 'height',     type: 'number', units: ['px', '%', 'vh', 'em'],  defaults: 'auto' },
                { property: 'max-width',  type: 'number', units: ['px', '%', 'vw'],        defaults: 'none' },
                { property: 'min-height', type: 'number', units: ['px', 'vh'],             defaults: '0' },
              ],
            },
            {
              name: 'Spacing',
              open: false,
              properties: [
                {
                  label: 'Padding',
                  property: 'padding',
                  type: 'composite',
                  properties: [
                    { property: 'padding-top',    type: 'number', units: ['px','%','rem'], defaults: '0' },
                    { property: 'padding-right',  type: 'number', units: ['px','%','rem'], defaults: '0' },
                    { property: 'padding-bottom', type: 'number', units: ['px','%','rem'], defaults: '0' },
                    { property: 'padding-left',   type: 'number', units: ['px','%','rem'], defaults: '0' },
                  ],
                },
                {
                  label: 'Margin',
                  property: 'margin',
                  type: 'composite',
                  properties: [
                    { property: 'margin-top',    type: 'number', units: ['px','%','rem'], defaults: '0' },
                    { property: 'margin-right',  type: 'number', units: ['px','%','rem'], defaults: '0' },
                    { property: 'margin-bottom', type: 'number', units: ['px','%','rem'], defaults: '0' },
                    { property: 'margin-left',   type: 'number', units: ['px','%','rem'], defaults: '0' },
                  ],
                },
              ],
            },
            {
              name: 'Typography',
              open: false,
              properties: ['font-family','font-size','font-weight','color','line-height','letter-spacing','text-align'],
            },
            {
              name: 'Decorations',
              open: false,
              properties: ['background-color','border-radius','border','box-shadow','opacity'],
            },
            {
              name: 'Extra',
              open: false,
              properties: ['cursor','overflow','display'],
            },
          ],
        },
        deviceManager: {
          devices: [
            {
              name: 'Desktop',
              width: '',
            },
            {
              name: 'Tablet',
              width: '768px',
              widthMedia: '992px',
            },
            {
              name: 'Mobile',
              width: '320px',
              widthMedia: '768px',
            }
          ]
        }
      });
      ////////////////////////////////////////////////////////////////////

      editorRef.current.on('load', () => {
        // Remove fullscreen, preview, and view-code buttons from the options panel
        ['fullscreen', 'preview', 'export-template'].forEach(id => {
          try { editorRef.current.Panels.removeButton('options', id); } catch (_) {}
        });

        // Deactivate sw-visibility so all-element borders are hidden; borders show only on hover/select
        try {
          const swVisBtn = editorRef.current.Panels.getButton('options', 'sw-visibility');
          if (swVisBtn) swVisBtn.set('active', false);
        } catch (_) {}

        // Auto-open the Blocks panel by default
        try {
          const openBlocksBtn = editorRef.current.Panels.getButton('views', 'open-blocks');
          if (openBlocksBtn) openBlocksBtn.set('active', true);
        } catch (_) {}

        // Inject canvas CSS and keep it as the LAST stylesheet in the canvas head
        // GrapesJS dynamically injects .gjs-selected styles when elements are selected,
        // so we use a MutationObserver on <head> to always move our tag to the end.
        const injectCanvasBorderOverride = () => {
          try {
            const canvasDoc = editorRef.current?.Canvas?.getDocument();
            if (!canvasDoc || !canvasDoc.head) return;

            const applyStyle = () => {
              let style = canvasDoc.getElementById('sxi-canvas-borders');
              if (!style) {
                style = canvasDoc.createElement('style');
                style.id = 'sxi-canvas-borders';
                style.innerHTML = `
                  .gjs-dashed *[data-gjs-highlightable] { outline: none !important; outline-offset: 0 !important; }
                  * { outline: none !important; outline-offset: 0 !important; }
                  *:focus, *:focus-visible { outline: none !important; }
                  [contenteditable]:focus, [contenteditable]:focus-visible { outline: none !important; border-color: transparent !important; }

                  /* ── Column layout (flex-based, replaces table layout from addBasicStyle) ── */
                  .gjs-row, .sxi-gjs-row, [data-gjs-custom-name="Row"] {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                    padding: 8px 0 !important;
                  }
                  .gjs-cell, .sxi-gjs-cell, [data-gjs-custom-name="Cell"] {
                    flex: 1 !important;
                    box-sizing: border-box !important;
                    padding: 5px !important;
                    min-width: 0 !important;
                  }
                  /* 3/7 split variant */
                  .gjs-cell30, .sxi-gjs-cell30 { flex: 0 0 30% !important; }
                  .gjs-cell70, .sxi-gjs-cell70 { flex: 0 0 70% !important; }

                  /* ── Row/cell visual outlines ── */
                  .gjs-row, .sxi-gjs-row, [data-gjs-custom-name="Row"] {
                    outline: 1px dashed #a0b4cc !important;
                    outline-offset: 0 !important;
                    min-height: 40px !important;
                  }
                  .gjs-cell, .sxi-gjs-cell, [data-gjs-custom-name="Cell"] {
                    outline: 1px dashed #c0d4e8 !important;
                    outline-offset: 0 !important;
                    min-height: 40px !important;
                  }
                  /* Hover — brighter blue */
                  .gjs-row:hover, .sxi-gjs-row:hover, [data-gjs-custom-name="Row"]:hover,
                  .gjs-cell:hover, .sxi-gjs-cell:hover, [data-gjs-custom-name="Cell"]:hover {
                    outline: 1px dashed #3b97e3 !important;
                  }
                  /* Selected component — solid brand outline */
                  .gjs-selected {
                    outline: 2px solid rgb(19, 40, 72) !important;
                    outline-offset: 1px !important;
                  }
                  /* Hovered but not selected — soft dashed */
                  .gjs-hovered:not(.gjs-selected) {
                    outline: 1px dashed #3b97e3 !important;
                  }
                  /* Drag-over drop zone highlight */
                  .sxi-drag-over {
                    outline: 2px dashed #f59e0b !important;
                    background-color: rgba(245, 158, 11, 0.08) !important;
                  }
                  /* Drag-drop insert position indicator */
                  .gjs-placeholder {
                    border-color: #3b97e3 !important;
                    background-color: rgba(59, 151, 227, 0.18) !important;
                    opacity: 1 !important;
                    min-height: 3px !important;
                  }
                  .gjs-placeholder.horizontal {
                    border-top: 2px solid #3b97e3 !important;
                  }
                `;
              }
              // Always append to end so we win over any GrapesJS-injected sheets
              canvasDoc.head.appendChild(style);
            };

            applyStyle();

            // Re-append our style whenever GrapesJS adds a new stylesheet to the canvas head
            const headObserver = new MutationObserver(() => {
              const style = canvasDoc.getElementById('sxi-canvas-borders');
              if (!style || style !== canvasDoc.head.lastElementChild) {
                applyStyle();
              }
            });
            headObserver.observe(canvasDoc.head, { childList: true });

            // Strip gjs-dashed class from canvas body and keep it off
            const canvasBody = editorRef.current?.Canvas?.getBody();
            if (canvasBody) {
              canvasBody.classList.remove('gjs-dashed');
              const bodyObserver = new MutationObserver(() => {
                if (canvasBody.classList.contains('gjs-dashed')) {
                  canvasBody.classList.remove('gjs-dashed');
                }
              });
              bodyObserver.observe(canvasBody, { attributes: true, attributeFilter: ['class'] });
            }
          } catch (_) {}
        };
        setTimeout(injectCanvasBorderOverride, 300);

        // Enable full resize (all 8 handles) on video and image component types
        // onEnd is required to sync the dragged size back to the component model
        try {
          const resizableCfg = {
            tl: 1, tc: 1, tr: 1, cl: 1, cr: 1, bl: 1, bc: 1, br: 1,
            onEnd({ el, component }) {
              try {
                const styles = {};
                if (el.style.width)  styles.width  = el.style.width;
                if (el.style.height) styles.height = el.style.height;
                if (Object.keys(styles).length) component.addStyle(styles);
              } catch (_) {}
            },
          };
          editorRef.current.DomComponents.addType('video', {
            model: { defaults: { resizable: resizableCfg } },
          });
          editorRef.current.DomComponents.addType('image', {
            model: { defaults: { resizable: resizableCfg } },
          });
          // addType only sets defaults for NEW instances — also patch already-loaded components
          editorRef.current.getWrapper()?.onAll(cmp => {
            try {
              const t = cmp.get('type');
              if (t === 'video' || t === 'image' || t === 'iframe') {
                cmp.set('resizable', resizableCfg);
              }
            } catch (_) {}
          });
        } catch (_) {}

        // Directly clear outline on the selected element's inline style (beats all CSS cascade)
        editorRef.current.on('component:selected', (component) => {
          try {
            const el = component.getEl();
            if (el) {
              el.style.setProperty('outline', 'none', 'important');
              el.style.setProperty('outline-offset', '0', 'important');
            }
          } catch (_) {}
          try {
            editorRef.current.StyleManager.getSectors().forEach(s => {
              const name = s.get('name');
              if (name === 'Dimensions' || name === 'Spacing') s.set('open', true);
            });
          } catch (_) {}
        });
        editorRef.current.on('component:deselected', (component) => {
          try {
            const el = component.getEl();
            if (el) {
              el.style.removeProperty('outline');
              el.style.removeProperty('outline-offset');
            }
          } catch (_) {}
        });

        // Remove video and map blocks from the blocks panel
        ['video', 'map'].forEach(id => {
          try { editorRef.current.BlockManager.remove(id); } catch (_) {}
        });

        // Custom sxi layout blocks — use gjs- prefix so prefixNewComponent keeps classes intact
        editorRef.current.BlockManager.add('sxi-2col', {
          label: '2 Columns',
          category: 'Layout',
          content: `<div class="gjs-row" data-gjs-droppable=".gjs-cell" data-gjs-custom-name="Row"><div class="gjs-cell" data-gjs-draggable=".gjs-row" data-gjs-custom-name="Cell"></div><div class="gjs-cell" data-gjs-draggable=".gjs-row" data-gjs-custom-name="Cell"></div></div>`,
          attributes: { class: 'fa fa-columns' },
        });
        editorRef.current.BlockManager.add('sxi-3col', {
          label: '3 Columns',
          category: 'Layout',
          content: `<div class="gjs-row" data-gjs-droppable=".gjs-cell" data-gjs-custom-name="Row"><div class="gjs-cell" data-gjs-draggable=".gjs-row" data-gjs-custom-name="Cell"></div><div class="gjs-cell" data-gjs-draggable=".gjs-row" data-gjs-custom-name="Cell"></div><div class="gjs-cell" data-gjs-draggable=".gjs-row" data-gjs-custom-name="Cell"></div></div>`,
          attributes: { class: 'fa fa-th' },
        });

        // Hide default GrapesJS device selector and move top-right panels to bottom-left
        setTimeout(() => {
          const deviceSelectors = document.querySelectorAll('.gjs-devices-c, .gjs-devices, .gjs-devices-container');
          deviceSelectors.forEach(el => {
            if (el) el.style.display = 'none';
          });

          // Move top-right GrapesJS panels to a fixed bottom-left wrapper
          const visualEditor = document.querySelector('.visual-editor');
          const optionsPanel = document.querySelector('.gjs-pn-options');
          const viewsPanel = document.querySelector('.gjs-pn-views');

          if (visualEditor && (optionsPanel || viewsPanel)) {
            let wrapper = document.querySelector('.gjs-panels-bottom-left');
            if (!wrapper) {
              wrapper = document.createElement('div');
              wrapper.className = 'gjs-panels-bottom-left';
              const isSidebarCollapsed = document.getElementById('sidebar')?.classList.contains('sidebar-collapsed');
              wrapper.style.cssText = [
                'position:fixed',
                'bottom:2px',
                `left:${isSidebarCollapsed ? '64px' : '280px'}`,
                'z-index:999',
                'display:flex',
                'flex-direction:row',
                'align-items:center',
                'background-color:#0d1b3',
                // 'border-top:1px solid #1f3a6c',
                // 'border-right:1px solid #1f3a6c',
                'border-radius:0 4px 0 0',
              ].join(';');
              document.body.appendChild(wrapper);
            }
            if (optionsPanel && optionsPanel.parentElement !== wrapper) {
              // Reset absolute positioning so panel renders inside flex wrapper
              optionsPanel.style.position = 'relative';
              optionsPanel.style.top = 'auto';
              optionsPanel.style.right = 'auto';
              optionsPanel.style.left = 'auto';
              optionsPanel.style.display = 'flex';
              wrapper.appendChild(optionsPanel);
            }
            if (viewsPanel && viewsPanel.parentElement !== wrapper) {
              viewsPanel.style.position = 'relative';
              viewsPanel.style.top = 'auto';
              viewsPanel.style.right = 'auto';
              viewsPanel.style.left = 'auto';
              viewsPanel.style.display = 'flex';
              wrapper.appendChild(viewsPanel);
            }
          }
        }, 100);
        
        // Sync device selector with GrapesJS device manager
        const deviceManager = editorRef.current.DeviceManager;
        if (deviceManager) {
          // Set initial device
          const selectedDevice = deviceManager.getSelected();
          if (selectedDevice) {
            const deviceName = selectedDevice.getName ? selectedDevice.getName().toLowerCase() : '';
            if (deviceName.includes('desktop')) {
              setCurrentDevice('desktop');
            } else if (deviceName.includes('tablet')) {
              setCurrentDevice('tablet');
            } else if (deviceName.includes('mobile')) {
              setCurrentDevice('mobile');
            }
          }
          
          // Listen for device changes from GrapesJS
          editorRef.current.on('change:device', () => {
            const selected = deviceManager.getSelected();
            if (selected) {
              const deviceName = selected.getName ? selected.getName().toLowerCase() : '';
              if (deviceName.includes('desktop')) {
                setCurrentDevice('desktop');
              } else if (deviceName.includes('tablet')) {
                setCurrentDevice('tablet');
              } else if (deviceName.includes('mobile')) {
                setCurrentDevice('mobile');
              }
            }
          });
        }
        
        // Right panel open/close — show when a component is selected, hide when nothing is selected
        const gjsContainer = document.getElementById('gjs');
        let deviceChanging = false;
        let panelCloseTimer = null;

        editorRef.current.on('change:device', () => {
          // Flag so deselect during device switch doesn't close the panel
          deviceChanging = true;
          clearTimeout(panelCloseTimer);
          setTimeout(() => { deviceChanging = false; }, 300);
        });

        editorRef.current.on('component:selected', () => {
          clearTimeout(panelCloseTimer);
          gjsContainer?.classList.add('sxi-panel-open');
          document.documentElement.style.setProperty('--sxi-right-panel-w', '260px');
        });
        editorRef.current.on('component:deselected', () => {
          if (deviceChanging) return;
          clearTimeout(panelCloseTimer);
          // Longer debounce (200ms) so rapid select/deselect cycles don't flicker
          panelCloseTimer = setTimeout(() => {
            if (editorRef.current && !editorRef.current.getSelected()) {
              gjsContainer?.classList.remove('sxi-panel-open');
              document.documentElement.style.setProperty('--sxi-right-panel-w', '0px');
            }
          }, 200);
        });

        requestAnimationFrame(injectJs);

        // Debounced injection
        let injectTimeout;
        const scheduleInjection = () => {
          clearTimeout(injectTimeout);
          injectTimeout = setTimeout(() => {
            requestAnimationFrame(injectJs);
          }, 100);
        };

        // Script injection events
        const injectionEvents = [
          'component:add',
          'component:remove',
          'component:update',
          'component:styleUpdate',
          'canvas:rerender',
          'block:drag:stop'
        ];

        injectionEvents.forEach(ev => editorRef.current.on(ev, scheduleInjection));

        // SIMPLIFIED: Prefix only newly created/updated components; ensure images get unique class
        const preserveId = (id) => {
          if (!id) return false;
          return id.startsWith('answer_') ||
            id.startsWith('toggle_') ||
            /^sxi_dynamic_carousel_\d+/.test(id) ||
            /^dynamic_template_faq_\d+/.test(id) ||
            id.startsWith('sxi-') ||
            id.startsWith('sxi_');
        };
        const prefixNewComponent = (cmp) => {
          try {
            const component = Array.isArray(cmp) ? cmp[0] : cmp;
            if (!component || typeof component.get === 'undefined') return;

            const compType = component.get('type');
            const classes = component.getClasses ? (component.getClasses() || []) : [];
            const add = [];
            const remove = [];

            // Helper function to check if a class/ID already has sxi prefix
            const hasSxiPrefix = (str) => {
              if (!str) return false;
              return str.startsWith('sxi-') || str.startsWith('sxi_');
            };

            if (compType === 'image') {
              const attrs = component.getAttributes ? (component.getAttributes() || {}) : {};
              const currentId = attrs.id || component.getId() || '';
              const safeId = (currentId || `image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`).replace(/[^a-zA-Z0-9_-]/g, '-');
              const uniqueCls = `sxi-uid-${safeId}`;

              // Add prefixed version of gjs-* classes while keeping originals for layout
              // Only prefix if it doesn't already have sxi prefix
              classes.forEach(cls => {
                if (cls && cls.startsWith('gjs-') && !hasSxiPrefix(cls)) {
                  const prefixed = `sxi-${cls}`;
                  if (!classes.includes(prefixed)) {
                    add.push(prefixed);
                  }
                }
              });
              if (!classes.includes(uniqueCls)) add.push(uniqueCls);

              // Ensure ID exists and is prefixed
              if (!currentId) {
                component.addAttributes({ id: `sxi-image-${safeId}` });
              } else if (!preserveId(currentId) && !hasSxiPrefix(currentId)) {
                component.addAttributes({ id: `sxi-${currentId}` });
              }
            } else {
              // For gjs-* classes: keep original AND add prefixed version (for layout stability)
              // Only prefix if it doesn't already have sxi prefix
              classes.forEach(cls => {
                if (!cls) return;
                if (cls.startsWith('gjs-') && !hasSxiPrefix(cls)) {
                  const prefixed = `sxi-${cls}`;
                  if (!classes.includes(prefixed)) {
                    add.push(prefixed); // Add sxi-gjs-* while keeping gjs-*
                  }
                } else if (!cls.startsWith('gjs-') && !hasSxiPrefix(cls)) {
                  // Prefix other classes that don't already have sxi prefix
                  remove.push(cls);
                  add.push(`sxi-${cls}`);
                }
              });

              const attrs = component.getAttributes ? (component.getAttributes() || {}) : {};
              const currentId = attrs.id || component.getId() || '';
              if (!currentId) {
                const base = compType || 'comp';
                component.addAttributes({ id: `sxi-${base}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });
              } else if (!preserveId(currentId) && !hasSxiPrefix(currentId)) {
                component.addAttributes({ id: `sxi-${currentId}` });
              }
            }

            // Apply class changes
            remove.forEach(c => { try { component.removeClass(c); } catch { } });
            add.forEach(c => { try { component.addClass(c); } catch { } });
          } catch (e) {
            console.warn('prefixNewComponent failed:', e);
          }
        };
        ////////////////////////////////
        // Primary: when dropping a block
        editorRef.current.on('block:drag:stop', (component, block) => {
          if (!component) return;
          prefixNewComponent(component);
        });

        // Drop zone highlighting
        editorRef.current.on('component:dragover', (target) => {
          try { target?.addClass('sxi-drag-over'); } catch (_) {}
        });
        editorRef.current.on('component:dragend', () => {
          try {
            editorRef.current.getWrapper()?.find('.sxi-drag-over')
              .forEach(c => { try { c.removeClass('sxi-drag-over'); } catch (_) {} });
          } catch (_) {}
        });

        // On component add/update
        editorRef.current.on('component:add', (cmp) => prefixNewComponent(cmp));
        editorRef.current.on('component:update', (cmp) => prefixNewComponent(cmp));
        editorRef.current.on('component:update:classes', (cmp) => prefixNewComponent(cmp));
        editorRef.current.on('component:update:attributes', (cmp) => prefixNewComponent(cmp));

        // For images, also ensure uniqueness on style changes
        editorRef.current.on('component:styleUpdate', (cmp) => {
          if (!cmp || cmp.get('type') !== 'image') return;
          prefixNewComponent(cmp);
          try {
            const st = cmp.getStyle ? (cmp.getStyle() || {}) : {};
            if (Object.keys(st).length && typeof cmp.setStyle === 'function') {
              cmp.setStyle({ ...st });
            }
          } catch { }
        });

        // Initial pass: prefix all existing components at load
        try {
          const wrapper = editorRef.current.getWrapper && editorRef.current.getWrapper();
          const walk = (c) => {
            if (!c) return;
            if (c.get) prefixNewComponent(c);
            const children = c.components ? c.components() : null;
            if (children && typeof children.forEach === 'function') children.forEach(ch => walk(ch));
          };
          if (wrapper) walk(wrapper);
        } catch (e) {
          console.warn('Initial component prefixing failed:', e);
        }
      });
    };

    fetchAndLoadHtml();

    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
      // Remove the bottom-left panel wrapper that was appended directly to document.body —
      // it is not part of the React tree so it survives component unmount without this.
      const bottomPanel = document.querySelector('.gjs-panels-bottom-left');
      if (bottomPanel) bottomPanel.remove();
      // Remove right-panel open class so it doesn't bleed into other pages
      document.getElementById('gjs')?.classList.remove('sxi-panel-open');
    };
  }, [injectJs, isProbablyJS]);

  // Watch #sidebar for collapse/expand class changes
  useEffect(() => {
    const sidebar = document.getElementById('sidebar');
    if (!sidebar) return;
    setSidebarCollapsed(sidebar.classList.contains('sidebar-collapsed'));
    const observer = new MutationObserver(() => {
      setSidebarCollapsed(sidebar.classList.contains('sidebar-collapsed'));
    });
    observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Sync gjs-panels-bottom-left, modal container, and bottom toolbar with sidebar width
  useEffect(() => {
    const sidebarW = sidebarCollapsed ? '64px' : '280px';
    const panel = document.querySelector('.gjs-panels-bottom-left');
    if (panel) panel.style.left = sidebarW;
    // CSS variables drive both the modal override and the bottom toolbar left edge
    document.documentElement.style.setProperty('--sxi-sidebar-w', sidebarW);
  }, [sidebarCollapsed]);

  ///////////////////
  /** Remove original gjs-* layout classes from HTML (keep only sxi-gjs-* versions) */
  // const removeGjsClassesFromHtml = useCallback((html) => {
  //   if (!html) return html;

  //   return html
  //     // Remove only exact gjs-row and gjs-cell classes (not prefixed with sxi-)
  //     .replace(/\b(gjs-row)\b/g, '')
  //     .replace(/\b(gjs-cell)\b/g, '')
  //     // Clean up multiple spaces
  //     .replace(/\s+/g, ' ')
  //     // Clean up class attributes
  //     .replace(/class="\s+"/g, 'class=""')
  //     .replace(/class="\s+([^"]+)"/g, 'class="$1"')
  //     .replace(/class="([^"]*)\s+"/g, 'class="$1"')
  //     // Remove empty class attributes
  //     .replace(/\s+class=""/g, '');
  // }, []);
  ///////////////////
  const removeGjsClassesFromHtml = useCallback((html) => {
    if (!html) return html;

    return html
      // More precise replacement that handles class attributes specifically
      .replace(/class="([^"]*)"/g, (match, classList) => {
        // Remove only gjs-row and gjs-cell from the class list
        const newClassList = classList
          .split(/\s+/)
          .filter(cls => cls !== 'gjs-row' && cls !== 'gjs-cell')
          .join(' ')
          .trim();

        return newClassList ? `class="${newClassList}"` : '';
      })
      // Clean up any resulting multiple spaces in the entire HTML
      .replace(/\s+/g, ' ')
      .trim();
  }, []);
  ////////////////////////////////////////////
  /** Removes duplicate CSS rule blocks from a combined CSS string */
  const deduplicateCSSRules = (css) => {
    if (!css) return '';
    const rules = [];
    const seen = new Set();
    let current = '';
    let depth = 0;
    for (let i = 0; i < css.length; i++) {
      const ch = css[i];
      if (ch === '{') depth++;
      if (ch === '}') depth--;
      current += ch;
      if (depth === 0 && ch === '}') {
        const normalized = current.trim().replace(/\s+/g, ' ');
        if (normalized && !seen.has(normalized)) {
          seen.add(normalized);
          rules.push(current.trim());
        }
        current = '';
      }
    }
    if (current.trim()) rules.push(current.trim());
    return rules.join('\n');
  };

  /** --- SAVE / COPY --- */
  const saveSection = async (sectionName) => {
    if (!editorRef.current) return null;

    let body = removeIdsFromHtml(editorRef.current.getHtml());
    body = removeGjsClassesFromHtml(body);
    const editorCss = cleanCssOutput(editorRef.current.getCss());
    // gridCss goes LAST so it always wins over any stale rules; !important as extra guard
    const gridCss = `
      .sxi-gjs-row { display: flex; flex-wrap: wrap; box-sizing: border-box; }
      .sxi-gjs-row > .sxi-gjs-cell { flex: 1 1 0 !important; box-sizing: border-box; }
      .sxi-gjs-cell { min-width: 0; box-sizing: border-box; }
    `;
    // GrapesJS getCss() already contains all original styles (loaded at init) — drop originalCss
    // to prevent shorthand vs longhand duplication on every save cycle.
    const cleanEditorCss = (c) => c
      .replace(/font-family\s*:[^;]+;?\s*/g, '')
      // Remove cursor from .sxi-container{} only — carousel buttons / FAQ rows keep theirs
      .replace(/(\.sxi-container\s*\{[^}]*)cursor\s*:[^;]+;?\s*/g, '$1');
    const css = deduplicateCSSRules(cleanEditorCss(`${editorCss}\n${gridCss}`.trim()));
    const js = scriptRef.current;

    let finalHtml = generateFinalHtml(body, css, js);
    finalHtml = normalizeVimeoEmbedUrls(finalHtml);
    const sanitizedFinalHtml = sanitizeHtmlContent(finalHtml);
    console.log("===============save Final html=====================");
    console.log(sanitizedFinalHtml);
    console.log("=================save final html ==============");
    await axios.post(`${app_url}/api/firebase/upload_section_internal`, {
      client_folder: Cookies.get('shop'),
      section_folder: sectionName,
      html_content: sanitizedFinalHtml,
      css_content: '',
      section_type: sessionStorage.getItem('section_type') || 'assets',
    }, {
      withCredentials: true,
      headers: {
        firebase_token: Cookies.get('firebase_token') || sessionStorage.getItem('firebase_token') || ''
      }
    });

    return finalHtml;
  };


  const handleSave = async () => {
    setSaveBtnLoading(true);
    try {
      const sectionName =
        sessionStorage.getItem('current_section_id') || 'Unknown Section';
      const response = await saveSection(sectionName);
      console.log("===============response=====================");
      console.log(response);
      console.log("=================response ==============");
      toast.success('Section saved successfully');
      window.location.href = '/imported-sections';
    } catch (err) {
      toast.error('Save failed: ' + err.message);
    } finally {
      setSaveBtnLoading(false);
    }
  };

  const handleSaveCopy = () => {
    setCopyModalInput('');
    setShowCopyModal(true);
  };

  const handleCopyModalConfirm = async () => {
    const newName = copyModalInput.trim();
    if (!newName) return;
    if (!SECTION_NAME_RE.test(newName)) {
      toast.error('Section name must start with a letter and use only letters, numbers, and underscores.');
      return;
    }
    setShowCopyModal(false);
    setCopiedBtnLoading(true);
    try {
      const finalHtml = await saveSection(newName);
      setCopiedSectionName(newName);
      sessionStorage.setItem('current_section_id', newName);
      if (finalHtml) {
        sessionStorage.setItem('current_edit_div', finalHtml);
      }
      toast.success('Copied section saved successfully!');
    } catch (err) {
      toast.error('Failed to copy: ' + err.message);
    } finally {
      setCopiedBtnLoading(false);
    }
  };

  const handleCancel = () => {
    window.location.href = '/imported-sections';
  };


  const handleDeviceChange = (device) => {
    if (!editorRef.current) return;
    
    // Get GrapesJS device manager
    const deviceManager = editorRef.current.DeviceManager;
    if (!deviceManager) return;
    
    // Get all available devices
    const devices = deviceManager.getAll();
    
    // Find the device by name (case-insensitive)
    let deviceToSelect = null;
    if (device === 'desktop') {
      deviceToSelect = devices.find(d => {
        const name = d.getName ? d.getName().toLowerCase() : '';
        return name === 'desktop';
      });
    } else if (device === 'tablet') {
      deviceToSelect = devices.find(d => {
        const name = d.getName ? d.getName().toLowerCase() : '';
        return name === 'tablet';
      });
    } else if (device === 'mobile') {
      deviceToSelect = devices.find(d => {
        const name = d.getName ? d.getName().toLowerCase() : '';
        return name === 'mobile';
      });
    }
    
    // Select the device using its ID
    if (deviceToSelect) {
      const deviceId = deviceToSelect.get ? deviceToSelect.get('id') : deviceToSelect.id;
      if (deviceId) {
        deviceManager.select(deviceId);
        setCurrentDevice(device);
      }
    } else {
      // Fallback: try selecting by name directly
      try {
        if (device === 'desktop') {
          deviceManager.select('desktop');
        } else if (device === 'tablet') {
          deviceManager.select('tablet');
        } else if (device === 'mobile') {
          deviceManager.select('mobile');
        }
        setCurrentDevice(device);
      } catch (e) {
        console.warn('Device selection failed:', e);
      }
    }
  };

  /** --- UI --- */
  return (
    <div className='visual-editor'>
      <div className='bottom-toolbar'>
        <div className='device-selector'>
          <button
            className={`device-btn ${currentDevice === 'desktop' ? 'active' : ''}`}
            onClick={() => handleDeviceChange('desktop')}
            title="Desktop"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 4.16667C2.5 3.24619 3.24619 2.5 4.16667 2.5H15.8333C16.7538 2.5 17.5 3.24619 17.5 4.16667V12.5C17.5 13.4205 16.7538 14.1667 15.8333 14.1667H10.8333L12.5 16.6667H8.33333L10 14.1667H4.16667C3.24619 14.1667 2.5 13.4205 2.5 12.5V4.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className={`device-btn ${currentDevice === 'tablet' ? 'active' : ''}`}
            onClick={() => handleDeviceChange('tablet')}
            title="Tablet"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.16667 2.5C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5H4.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14.1667V15.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className={`device-btn ${currentDevice === 'mobile' ? 'active' : ''}`}
            onClick={() => handleDeviceChange('mobile')}
            title="Mobile"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.66667 2.5H13.3333C14.2538 2.5 15 3.24619 15 4.16667V15.8333C15 16.7538 14.2538 17.5 13.3333 17.5H6.66667C5.74619 17.5 5 16.7538 5 15.8333V4.16667C5 3.24619 5.74619 2.5 6.66667 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 13.3333V13.3417" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className='btn-group-right'>
          <button onClick={handleSave} className='btn-action' disabled={saveBtnLoading}>
            {saveBtnLoading ? (
              <Spinner animation='border' size="sm" style={{ marginRight: '6px' }} />
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
                <path d="M5.83333 2.5V5.33333C5.83333 5.80004 5.83333 6.0334 5.92416 6.21166C6.00406 6.36846 6.13154 6.49594 6.28834 6.57584C6.4666 6.66667 6.69996 6.66667 7.16667 6.66667H12.8333C13.3 6.66667 13.5334 6.66667 13.7117 6.57584C13.8685 6.49594 13.9959 6.36846 14.0758 6.21166C14.1667 6.0334 14.1667 5.80004 14.1667 5.33333V3.33333M14.1667 17.5V12.1667C14.1667 11.7 14.1667 11.4666 14.0758 11.2883C13.9959 11.1315 13.8685 11.0041 13.7117 10.9242C13.5334 10.8333 13.3 10.8333 12.8333 10.8333H7.16667C6.69996 10.8333 6.4666 10.8333 6.28834 10.9242C6.13154 11.0041 6.00406 11.1315 5.92416 11.2883C5.83333 11.4666 5.83333 11.7 5.83333 12.1667V17.5M17.5 7.77124V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H12.2288C12.6364 2.5 12.8402 2.5 13.0321 2.54605C13.2021 2.58688 13.3647 2.65422 13.5138 2.7456C13.682 2.84867 13.8261 2.9928 14.1144 3.28105L16.719 5.88562C17.0072 6.17387 17.1513 6.318 17.2544 6.48619C17.3458 6.63531 17.4131 6.79789 17.4539 6.96795C17.5 7.15976 17.5 7.36358 17.5 7.77124Z" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            Save & Return
          </button>
          <button onClick={handleSaveCopy} className='btn-action' disabled={copiedBtnLoading}>
            {copiedBtnLoading ? (
              <Spinner animation='border' size="sm" style={{ marginRight: '6px' }} />
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px' }}>
                <path d="M6 15H5.4C4.07 15 3 13.93 3 12.6V5.4C3 4.07 4.07 3 5.4 3H12.6C13.93 3 15 4.07 15 5.4V6M11.4 9H18.6C19.2365 9 19.847 9.25286 20.2971 9.70294C20.7471 10.153 21 10.7635 21 11.4V18.6C21 19.2365 20.7471 19.847 20.2971 20.2971C19.847 20.7471 19.2365 21 18.6 21H11.4C10.7635 21 10.153 20.7471 9.70294 20.2971C9.25286 19.847 9 19.2365 9 18.6V11.4C9 11.0848 9.06208 10.7727 9.18269 10.4816C9.3033 10.1904 9.48008 9.9258 9.70294 9.70294C9.9258 9.48008 10.1904 9.3033 10.4816 9.18269C10.7727 9.06208 11.0848 9 11.4 9Z" stroke="#ffffff" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
            Save As Copy
          </button>
          <button onClick={handleCancel} className='btn-action'>
            ← Back
          </button>
        </div>
      </div>

      {showCopyModal && (
        <div className='sxi-copy-modal-overlay' onClick={() => setShowCopyModal(false)}>
          <div className='sxi-copy-modal' onClick={e => e.stopPropagation()}>
            <div className='sxi-copy-modal-header'>
              <span>Save as Copy</span>
              <button className='sxi-copy-modal-close' onClick={() => setShowCopyModal(false)}>✕</button>
            </div>
            <div className='sxi-copy-modal-body'>
              <label className='sxi-copy-modal-label'>Section name</label>
              <input
                className='sxi-copy-modal-input'
                type='text'
                placeholder='Enter a name for the copied section'
                value={copyModalInput}
                onChange={e => setCopyModalInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCopyModalConfirm(); if (e.key === 'Escape') setShowCopyModal(false); }}
                autoFocus
              />
              {copyModalInput.trim() && !SECTION_NAME_RE.test(copyModalInput.trim()) && (
                <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#dc3545' }}>
                  Use letters, numbers, and underscores; must start with a letter.
                </div>
              )}
            </div>
            <div className='sxi-copy-modal-footer'>
              <button className='sxi-copy-modal-btn-confirm' onClick={handleCopyModalConfirm} disabled={!copyModalInput.trim() || !SECTION_NAME_RE.test(copyModalInput.trim())}>Save As Copy</button>
              <button className='sxi-copy-modal-btn-cancel' onClick={() => setShowCopyModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div id='gjs'></div>

      <style>{`
        .device-selector {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: 13rem;
        }
        .device-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          padding: 0;
          border-radius: 4px;
          color: rgb(5, 44, 101);
          border: none;
          cursor: pointer;
          background-color: transparent;
          transition: all 0.2s ease;
        }
        .device-btn:hover {
          background-color: #e8f4fd;
          color: rgb(5, 44, 101);
        }
        .device-btn.active {
          background-color: rgb(19, 40, 72);
          color: #ffffff;
        }
        .device-btn.active svg {
          stroke: #ffffff !important;
        }
        .device-btn svg {
          width: 20px;
          height: 20px;
        }
        .bottom-toolbar {
          left: var(--sxi-sidebar-w, 280px);
          right: var(--sxi-right-panel-w, 0px);
          background-color: #F8FAFC;
          border-top: 2px solid #ededed;
          padding: 8px 0px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: fixed;
          z-index: 4;
          bottom: 0;
          gap: 8px;
          padding-right: 10px;
          transition: left 0.2s ease, right 0.2s ease;
        }
        /* Ghost preview while dragging */
        .gjs-drag-helper {
          opacity: 0.6 !important;
          border: 1px dashed #3b97e3 !important;
          border-radius: 3px !important;
          pointer-events: none !important;
        }
        /* Resize handle dots — rendered in outer DOM, not inside iframe */
        .gjs-resizer-h {
          background: #ffffff !important;
          border: 2px solid rgb(19, 40, 72) !important;
          border-radius: 50% !important;
          width: 10px !important;
          height: 10px !important;
          pointer-events: all !important;
          z-index: 100 !important;
          box-shadow: 0 1px 4px rgba(0,0,0,0.25) !important;
        }
        .gjs-resizer-h:hover {
          background: rgb(19, 40, 72) !important;
          transform: scale(1.3) !important;
        }
        .gjs-resizing {
          outline: 2px solid #3b97e3 !important;
        }
        /* GrapesJS commands panel — full width so it doesn't clip */
        .gjs-pn-commands {
          width: 100% !important;
        }
        /* GrapesJS panels relocated to bottom-left wrapper */
        .gjs-panels-bottom-left .gjs-pn-panel {
          background-color: transparent !important;
          position: relative !important;
          display: flex !important;
        }
        .gjs-panels-bottom-left .gjs-pn-buttons {
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: nowrap !important;
          align-items: center !important;
        }
        .btn-back {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          width: 94px;
          height: 35px;
          padding: 10px 16px;
          border-radius: 8px;
          font-weight: 500;
          color: rgb(5, 44, 101);
          border: 1px solid #dee2e6;
          cursor: pointer;
          background-color: #ffffff;
          box-shadow: 0px 1px 2px 0px rgba(5, 44, 101, 0.08);
          white-space: nowrap;
          transition: all 0.2s ease;
        }
        .btn-back:hover {
          background-color: #e8f4fd;
          border-color: rgb(19, 40, 72);
          color: rgb(5, 44, 101);
        }
        .btn-back:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-group-right {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .btn-action {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          width: 140px;
          height: 34px;
          padding: 10px 16px;
          border-radius: 8px;
          font-weight: 500;
          color: #ffffff;
          border: none;
          cursor: pointer;
          background-color: rgb(19, 40, 72);
          box-shadow: 0px 1px 2px 0px rgba(5, 44, 101, 0.12);
          white-space: nowrap;
          transition: all 0.2s ease;
        }
        .btn-action:hover:not(:disabled) {
          // background-color: #0b85c4;
        }
        .btn-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-action .spinner-border,
        .btn-back .spinner-border {
          color: #ffffff !important;
          border-color: #ffffff transparent #ffffff transparent !important;
        }
        /* GrapesJS Theme Colors - Light Theme (matches app design system) */
        .gjs-editor,
        .gjs-pn-panel,
        .gjs-cv-canvas,
        .gjs-one-bg {
          background-color: #F8FAFC !important;
          color: #475666 !important;
        }
        .gjs-two-color {
          color: rgb(5, 44, 101) !important;
        }
        /* Blocks */
        .gjs-block {
          background-color: #ffffff !important;
          border: 1px solid #e9ecef !important;
          color: #475666 !important;
          border-radius: 6px !important;
        }
        .gjs-block:hover {
          background-color: #e8f4fd !important;
          border-color: rgb(19, 40, 72) !important;
          color: rgb(5, 44, 101) !important;
        }
        .gjs-block.gjs-block--selected,
        .gjs-block:active {
          background-color: #cce9f8 !important;
          border-color: rgb(19, 40, 72) !important;
          color: rgb(5, 44, 101) !important;
        }
        .gjs-block-category,
        .gjs-block-category .gjs-title,
        .gjs-block-category__title,
        .gjs-title {
          background-color: #ffffff !important;
          color: rgb(5, 44, 101) !important;
        }
        /* Panel buttons */
        .gjs-pn-btn {
          background-color: transparent !important;
          color: rgb(5, 44, 101) !important;
          border-color: #dee2e6 !important;
          min-width: 25px !important;
        }
        .gjs-pn-btn:hover {
          background-color: #e8f4fd !important;
          color: rgb(5, 44, 101) !important;
          border-color: rgb(19, 40, 72) !important;
        }
        .gjs-pn-btn.gjs-pn-active {
          background-color: rgb(19, 40, 72) !important;
          color: #ffffff !important;
          border-color: rgb(19, 40, 72) !important;
        }
        /* Property inputs and fields */
        .gjs-field {
          background-color: #ffffff !important;
        }
        .gjs-field input,
        .gjs-field select,
        .gjs-field textarea {
          background-color: #ffffff !important;
          color: #475666 !important;
          border: 1px solid #dee2e6 !important;
        }
        .gjs-field input:focus,
        .gjs-field select:focus,
        .gjs-field textarea:focus {
          background-color: #ffffff !important;
          border-color: rgb(19, 40, 72) !important;
          color: #475666 !important;
        }
        .gjs-field input::placeholder {
          color: rgba(71, 86, 102, 0.5) !important;
        }
        /* Unit selector — override the generic .gjs-field select border/sizing */
        .gjs-field-units {
          display: flex !important;
          align-items: stretch !important;
          flex-shrink: 0 !important;
        }
        .gjs-input-unit,
        .gjs-field-units select {
          min-width: 36px !important;
          width: auto !important;
          padding: 0 4px !important;
          text-align: center !important;
          font-size: 11px !important;
          border: none !important;
          border-left: 1px solid #dee2e6 !important;
          border-right: 1px solid #dee2e6 !important;
          border-radius: 0 !important;
          background-color: #f0f4f8 !important;
          color: #475666 !important;
          cursor: pointer !important;
          overflow: visible !important;
          appearance: auto !important;
          -webkit-appearance: auto !important;
        }
        /* Select dropdowns */
        .gjs-select,
        .gjs-field-select {
          background-color: #ffffff !important;
          border: 1px solid #dee2e6 !important;
          color: #475666 !important;
        }
        .gjs-select:hover,
        .gjs-field-select:hover {
          border-color: rgb(19, 40, 72) !important;
        }
        /* Toolbar and badges */
        .gjs-toolbar,
        .gjs-badge {
          background-color: rgb(19, 40, 72) !important;
          border: none !important;
          color: #ffffff !important;
          border-radius: 4px !important;
        }
        .gjs-badge__name {
          color: #ffffff !important;
        }
        /* Property manager */
        .gjs-sm-property,
        .gjs-sm-sector {
          background-color: transparent !important;
          color: #475666 !important;
        }
        .gjs-sm-label {
          color: rgb(5, 44, 101) !important;
          font-weight: 500 !important;
        }
        .gjs-sm-sector-title {
          color: rgb(5, 44, 101) !important;
          font-weight: 600 !important;
          background-color: #ffffff !important;
          border-bottom: 1px solid #e9ecef !important;
          padding: 8px 10px !important;
        }
        .gjs-sm-sector-title:hover {
          background-color: #e8f4fd !important;
        }
        /* Radio buttons and checkboxes */
        .gjs-radio-item input[type="radio"]:checked + label,
        .gjs-radio-item-label {
          color: #475666 !important;
        }
        .gjs-radio-item:hover {
          background-color: #e8f4fd !important;
        }
        /* Color picker */
        .gjs-field-color,
        .gjs-field-colorp {
          background-color: #ffffff !important;
          border: 1px solid #dee2e6 !important;
        }
        /* Layer manager */
        .gjs-lm-layer,
        .gjs-lm-item {
          background-color: transparent !important;
          color: #475666 !important;
        }
        .gjs-lm-layer:hover,
        .gjs-lm-item:hover {
          background-color: #e8f4fd !important;
        }
        /* Modal / dialog */
        .gjs-mdl-container {
          left: var(--sxi-sidebar-w, 280px) !important;
          width: calc(100% - var(--sxi-sidebar-w, 280px)) !important;
        }
        .gjs-mdl-dialog {
          background-color: #F8FAFC !important;
          color: #475666 !important;
          border: 1px solid #e9ecef !important;
          border-radius: 8px !important;
          max-width: min(900px, calc(100vw - var(--sxi-sidebar-w, 280px) - 40px)) !important;
          width: 100% !important;
        }
        .gjs-mdl-header {
          background-color: #ffffff !important;
          color: rgb(5, 44, 101) !important;
          border-bottom: 1px solid #e9ecef !important;
          font-weight: 700 !important;
        }
        /* Canvas tools */
        .gjs-tools,
        .gjs-highlighter {
          border-color: rgb(19, 40, 72) !important;
        }
        /* Frame wrapper */
        .gjs-frame-wrapper {
          border-color: #dee2e6 !important;
          bottom: 100px !important;
        }
        .gjs-frame-wrapper__top {
          background-color: #ffffff !important;
          color: rgb(5, 44, 101) !important;
          border-bottom: 1px solid #e9ecef !important;
        }
        /* Hide class/state selector manager - all elements */
        .gjs-sm-header,
        .gjs-clm-header,
        .gjs-clm-tags-c,
        .gjs-clm-tag,
        .gjs-clm-tags-btn,
        .gjs-clm-select,
        .gjs-clm-sel-id,
        .gjs-clm-status,
        .gjs-clm-first,
        .gjs-sm-sector .gjs-sm-sector__selector-manager,
        [class*="gjs-clm"] {
          display: none !important;
        }
        /* Tags and classes */
        .gjs-clm-tag,
        .gjs-clm-tags-btn {
          background-color: #e8f4fd !important;
          border: 1px solid rgb(19, 40, 72) !important;
          color: rgb(5, 44, 101) !important;
          border-radius: 4px !important;
        }
        .gjs-clm-tag:hover,
        .gjs-clm-tags-btn:hover {
          background-color: #cce9f8 !important;
        }
        /* Stack and composite fields */
        .gjs-sm-stack,
        .gjs-sm-composite {
          background-color: transparent !important;
        }
        /* Composite property sub-fields — ensure value inputs can shrink without clipping text */
        .gjs-sm-composite .gjs-field,
        .gjs-sm-stack .gjs-field {
          min-width: 0 !important;
          overflow: hidden !important;
        }
        .gjs-sm-composite .gjs-field input,
        .gjs-sm-stack .gjs-field input {
          min-width: 0 !important;
          flex: 1 1 0 !important;
          width: 100% !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }
        /* Input units — main rule above near .gjs-field; this block intentionally empty */
        /* Arrow buttons — CSS border-triangle shapes; color has no effect, must use border-color */
        .gjs-field-arrows {
          background-color: #f0f4f8 !important;
          border-left: 1px solid #dee2e6 !important;
          border-radius: 0 4px 4px 0 !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          align-items: center !important;
          padding: 0 4px !important;
          gap: 3px !important;
          min-width: 20px !important;
        }
        .gjs-field-arrow-u {
          border-bottom-color: rgb(5, 44, 101) !important;
          border-left-color: transparent !important;
          border-right-color: transparent !important;
          border-top-color: transparent !important;
          cursor: pointer !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        .gjs-field-arrow-d {
          border-top-color: rgb(5, 44, 101) !important;
          border-left-color: transparent !important;
          border-right-color: transparent !important;
          border-bottom-color: transparent !important;
          cursor: pointer !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        .gjs-field-arrow-u:hover {
          border-bottom-color: rgb(19, 40, 72) !important;
        }
        .gjs-field-arrow-d:hover {
          border-top-color: rgb(19, 40, 72) !important;
        }
        /* Clear button */
        .gjs-sm-clear {
          color: rgb(5, 44, 101) !important;
        }
        .gjs-sm-clear:hover {
          background-color: #e8f4fd !important;
        }
        /* RTE toolbar */
        .gjs-rte-toolbar,
        .gjs-rte-actionbar {
          background-color: #ffffff !important;
          border: 1px solid #e9ecef !important;
          border-radius: 6px !important;
          box-shadow: 0 2px 8px rgba(5,44,101,0.1) !important;
        }
        .gjs-rte-action {
          color: rgb(5, 44, 101) !important;
        }
        .gjs-rte-action:hover {
          background-color: #e8f4fd !important;
          color: rgb(19, 40, 72) !important;
        }
        /* Hide default GrapesJS device selector */
        .gjs-devices-c,
        .gjs-devices,
        .gjs-devices-container {
          display: none !important;
        }
        [class*="sxi-"] { all: unset; }
        /* Right panel — hidden by default, opens when a component is selected */
        .gjs-pn-views-container {
          width: 0 !important;
          min-width: 0 !important;
          max-width: 0 !important;
          overflow: hidden !important;
          padding: 0 !important;
          transition: width 0.2s ease, min-width 0.2s ease !important;
        }
        #gjs.sxi-panel-open .gjs-pn-views-container {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          padding: 20px 0px 0px !important;
          scrollbar-width: thin !important;
          scrollbar-color: rgba(19,40,72,0.3) transparent !important;
        }
        #gjs.sxi-panel-open .gjs-pn-views-container::-webkit-scrollbar {
          width: 4px !important;
        }
        #gjs.sxi-panel-open .gjs-pn-views-container::-webkit-scrollbar-track {
          background: transparent !important;
        }
        #gjs.sxi-panel-open .gjs-pn-views-container::-webkit-scrollbar-thumb {
          background-color: rgba(19,40,72,0.25) !important;
          border-radius: 4px !important;
        }
        #gjs.sxi-panel-open .gjs-pn-views-container::-webkit-scrollbar-thumb:hover {
          background-color: rgba(19,40,72,0.5) !important;
        }
        .gjs-cv-canvas {
          right: 0 !important;
          left: 0 !important;
          width: auto !important;
          margin: 0.5rem !important;
          transition: right 0.2s ease !important;
        }
        #gjs.sxi-panel-open .gjs-cv-canvas {
          right: 260px !important;
        }
        /* Save & Copy modal */
        .sxi-copy-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(5, 44, 101, 0.18);
          backdrop-filter: blur(2px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sxi-copy-modal {
          background: #ffffff;
          border: 1px solid #e9ecef;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(5, 44, 101, 0.14);
          width: 420px;
          max-width: 94vw;
          overflow: hidden;
        }
        .sxi-copy-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          background: #F8FAFC;
          border-bottom: 1px solid #e9ecef;
          font-size: 15px;
          font-weight: 700;
          color: rgb(5, 44, 101);
        }
        .sxi-copy-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #475666;
          font-size: 16px;
          line-height: 1;
          padding: 2px 6px;
          border-radius: 4px;
          transition: background 0.15s;
        }
        .sxi-copy-modal-close:hover {
          background: #e8f4fd;
          color: rgb(5, 44, 101);
        }
        .sxi-copy-modal-body {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .sxi-copy-modal-label {
          font-size: 13px;
          font-weight: 600;
          color: rgb(5, 44, 101);
        }
        .sxi-copy-modal-input {
          width: 100%;
          height: 30px;
          padding: 4px 6px;
          margin-left:-5px;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          font-size: 14px;
          color: #475666;
          background: #ffffff;
          outline: none;
          transition: border-color 0.15s;
        }
        .sxi-copy-modal-input:focus {
          // border-color: #0e98e1;
          box-shadow: 0 0 0 3px rgba(14, 152, 225, 0.12);
        }
        .sxi-copy-modal-input::placeholder {
          color: rgba(71, 86, 102, 0.5);
        }
        .sxi-copy-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 7px 10px;
          background: #F8FAFC;
          border-top: 1px solid #e9ecef;
        }
        .sxi-copy-modal-btn-cancel {
          height: 34px;
          padding: 0 16px;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          background: #ffffff;
          color: rgb(5, 44, 101);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }
        .sxi-copy-modal-btn-cancel:hover {
          background: #e8f4fd;
          // border-color: #0e98e1;
        }
        .sxi-copy-modal-btn-confirm {
          height: 34px;
          padding: 0 18px;
          border: none;
          border-radius: 8px;
          background: rgb(19, 40, 72);
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }
        .sxi-copy-modal-btn-confirm:hover:not(:disabled) {
          // background: #0b85c4;
        }
        .sxi-copy-modal-btn-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default FullEditor;
